import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/SimpleToast";
import { Shield, ShieldCheck, ShieldOff, Loader2, QrCode, Key, CheckCircle } from "lucide-react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = '/api';

export function TwoFactorSettings({ toast: externalToast }) {
  const internalToast = useToast();
  const toast = externalToast || internalToast;
  
  const [status, setStatus] = useState({ enabled: false, verified: false });
  const [loading, setLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: QR, 2: Verify

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/totp/status`);
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch TOTP status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/auth/totp/setup`);
      setSetupData(response.data);
      setSetupDialogOpen(true);
      setSetupStep(1);
    } catch (error) {
      toast.error("Fehler beim Einrichten der 2FA");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/totp/verify`, { code: verificationCode });
      toast.success("2FA erfolgreich aktiviert!");
      setSetupDialogOpen(false);
      setVerificationCode("");
      setSetupData(null);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ungültiger Code");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/auth/totp/disable`, { code: disableCode });
      toast.success("2FA deaktiviert");
      setDisableDialogOpen(false);
      setDisableCode("");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ungültiger Code");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const isActive = status.enabled && status.verified;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isActive ? 'bg-green-500/10' : 'bg-muted'}`}>
                {isActive ? (
                  <ShieldCheck className="w-5 h-5 text-green-500" />
                ) : (
                  <Shield className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <CardTitle className="text-lg">2-Faktor-Authentifizierung</CardTitle>
                <CardDescription>
                  Zusätzliche Sicherheit mit Google Authenticator
                </CardDescription>
              </div>
            </div>
            <Badge variant={isActive ? "default" : "secondary"} className={isActive ? "bg-green-500" : ""}>
              {isActive ? "Aktiv" : "Inaktiv"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
            <p className="font-medium">Was ist 2-Faktor-Authentifizierung?</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Schützt Ihr Konto auch wenn Ihr Passwort bekannt wird</li>
              <li>Bei jeder Anmeldung wird ein 6-stelliger Code benötigt</li>
              <li>Der Code wird in der Google Authenticator App generiert</li>
            </ul>
          </div>

          {isActive ? (
            <Button 
              variant="destructive" 
              onClick={() => setDisableDialogOpen(true)}
              className="gap-2"
              data-testid="disable-2fa-btn"
            >
              <ShieldOff className="w-4 h-4" />
              2FA deaktivieren
            </Button>
          ) : (
            <Button 
              onClick={handleSetup}
              disabled={submitting}
              className="gap-2"
              data-testid="setup-2fa-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              2FA einrichten
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {setupStep === 1 ? (
                <>
                  <QrCode className="w-5 h-5 text-primary" />
                  QR-Code scannen
                </>
              ) : (
                <>
                  <Key className="w-5 h-5 text-primary" />
                  Code verifizieren
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {setupStep === 1 
                ? "Scannen Sie den QR-Code mit Google Authenticator" 
                : "Geben Sie den 6-stelligen Code aus der App ein"
              }
            </DialogDescription>
          </DialogHeader>

          {setupStep === 1 && setupData && (
            <div className="space-y-4 py-4">
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg">
                  <img 
                    src={setupData.qr_code} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                    data-testid="totp-qr-code"
                  />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Oder geben Sie diesen Code manuell ein:
                </p>
                <code className="block p-2 bg-muted rounded text-sm font-mono break-all">
                  {setupData.secret}
                </code>
              </div>
            </div>
          )}

          {setupStep === 2 && (
            <div className="space-y-4 py-4">
              <div className="flex justify-center py-2">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="verify-code">Verifizierungscode</Label>
                <Input
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  autoFocus
                  data-testid="totp-verify-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {setupStep === 1 ? (
              <Button 
                onClick={() => setSetupStep(2)} 
                className="w-full"
                data-testid="totp-next-btn"
              >
                Weiter
              </Button>
            ) : (
              <div className="flex gap-2 w-full">
                <Button 
                  variant="outline" 
                  onClick={() => setSetupStep(1)}
                  className="flex-1"
                >
                  Zurück
                </Button>
                <Button 
                  onClick={handleVerify}
                  disabled={submitting || verificationCode.length !== 6}
                  className="flex-1"
                  data-testid="totp-verify-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Aktivieren"
                  )}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="w-5 h-5 text-destructive" />
              2FA deaktivieren
            </DialogTitle>
            <DialogDescription>
              Geben Sie Ihren aktuellen 2FA-Code ein, um die Zwei-Faktor-Authentifizierung zu deaktivieren.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-code">Authentifizierungscode</Label>
              <Input
                id="disable-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
                data-testid="totp-disable-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDisableDialogOpen(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDisable}
              disabled={submitting || disableCode.length !== 6}
              className="flex-1"
              data-testid="totp-disable-confirm-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Deaktivieren"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
